import { AddressId } from '../domain/entities/Address';
import ShippingRepository from '../repositories/ShippingRepository';
import { diContainer, serviceNames } from '../diContainer/diContainer';
import BillingRepository from '../repositories/BillingRepository';
import ShippingUIState from '../helpers/UIState/ShippingUIState';
import Config from '../domain/entities/Config';
import TransactionLoadHandler, { ITransactionLoad } from './TransactionLoadHandler';
import { FIELD_TYPE } from '../constants/Constants';
import NotificationsCollection from '../domain/entities/Notification/NotificationsCollection';
import { RESTRICTION_ERRORS } from '../constants/Errors';

class SelectShippingAddressUseCase extends TransactionLoadHandler {
  private id: AddressId;

  private shippingRepository: ShippingRepository;

  private billingRepository: BillingRepository;

  private isBillingSameAsShipping: boolean;

  private shouldSaveAddress: boolean;

  private shippingUIState: ShippingUIState;

  private config: Config;

  private notificationsCollection: NotificationsCollection;

  constructor(id: AddressId, isBillingSameAsShipping: boolean, shouldSaveAddress = true, options?: ITransactionLoad) {
    super({ ...options?.transactionLoadOptions, isControlled: true });
    this.id = id;
    this.isBillingSameAsShipping = isBillingSameAsShipping;
    this.shouldSaveAddress = shouldSaveAddress;
    this.shippingRepository = diContainer.get(serviceNames.shippingRepository);
    this.transactionRepository = diContainer.get(serviceNames.transactionRepository);
    this.billingRepository = diContainer.get(serviceNames.billingRepository);
    this.shippingUIState = diContainer.get(serviceNames.shippingUIState);
    this.config = diContainer.get(serviceNames.config);
    this.notificationsCollection = diContainer.get(serviceNames.notificationsCollection);
  }

  public run = async (): Promise<void> => {
    const transaction = await this.transactionRepository.getTransaction();
    const selectedAddress = this.shippingRepository.getAddressById(this.id);
    try {
      this.shippingUIState.updateContinueLoadingButtonState(true);
      // reload shipping methods if new selected country is different from previous saved transaction countryId
      // and multiple countries can be used
      const transactionAddressCountryId = transaction?.shippingAddress?.attributes?.[FIELD_TYPE.COUNTRY_ID];
      const formStoreCountryId = this.shippingRepository.formStore.values[FIELD_TYPE.COUNTRY_ID];
      const previousCountrySelection = transactionAddressCountryId || formStoreCountryId;
      const shouldLoadShippingMethods = (this.config.shippingCountries?.length > 1 &&
        selectedAddress?.attributes?.[FIELD_TYPE.COUNTRY_ID] !== previousCountrySelection) || this.config.useShippingCalculator;

      transaction.setShippingAddress(selectedAddress);
      if (shouldLoadShippingMethods) {
        await this.transactionRepository.loadShippingMethods(selectedAddress.attributes[FIELD_TYPE.COUNTRY_ID], {
          debounceRequest: false,
          reloadTransaction: false,
        });
      }
      if (this.isBillingSameAsShipping) {
        transaction.setBillingAddress(selectedAddress);
      }

      if (!this.shouldSaveAddress) {
        if (this.shippingRepository.config?.useShippingCalculator) {
          // Flow: User is canceling the new address he was trying to add, but order summary might have changed during shippingCalc api call.
          // We need to refresh the order summary using current (previous) address selection, hence the call below.
          this.transactionRepository.loadTransaction();
        }
        this.shippingUIState.updateContinueLoadingButtonState(false);
        return;
      }

      await this.shippingRepository.onSelectAddress(this.id);
      if (this.isBillingSameAsShipping) {
        await this.billingRepository.selectAddress(this.id);
      }

      if (this.notificationsCollection.hasNotification(RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS_MILITARY)) {
        this.notificationsCollection.clearNotificationByKey(RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS_MILITARY);
      }

      if (this.notificationsCollection.hasNotification(RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS)) {
        this.notificationsCollection.clearNotificationByKey(RESTRICTION_ERRORS.RESTRICTION_APO_ADDRESS);
      }

      await this.handleTransactionUpdate();
      this.shippingUIState.updateContinueLoadingButtonState(false);
    } catch {
      this.shippingUIState.updateContinueLoadingButtonState(false);
    }
  };
}

export default SelectShippingAddressUseCase;
